import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskedLine } from "../Reveal";

const BANNER_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_atelier-visas/artifacts/syueh9gw_The_Visa_Atelier_Banner.jpg";

const EASE = [0.22, 1, 0.36, 1];
const FADE_HIDDEN = { opacity: 0 };
const FADE_SHOWN = { opacity: 1 };
const META_TRANSITION = { duration: 1, delay: 1.4 };
const BANNER_HIDDEN = { opacity: 0, y: 30, scale: 0.98 };
const BANNER_SHOWN = { opacity: 1, y: 0, scale: 1 };
const BANNER_TRANSITION = { duration: 1.4, delay: 0.25, ease: EASE };
const CTA_HIDDEN = { opacity: 0, y: 20 };
const CTA_SHOWN = { opacity: 1, y: 0 };
const CTA_TRANSITION = { duration: 1, delay: 1.2, ease: EASE };

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-navy"
      data-testid="hero-section"
    >
      {/* Top meta bar */}
      <motion.div
        initial={FADE_HIDDEN}
        animate={FADE_SHOWN}
        transition={META_TRANSITION}
        className="absolute top-24 md:top-28 right-6 md:right-10 z-20 text-right"
      >
        <div className="font-mono-ui text-sm tracking-[0.22em] text-ivory/60 uppercase">
          Immigration · Craft · Discretion
        </div>
      </motion.div>

      <div className="relative z-10 min-h-[100svh] flex flex-col justify-start px-6 md:px-10 max-w-[1400px] mx-auto pt-40 md:pt-48 pb-24 md:pb-28">
        <motion.div style={{ opacity }} className="flex flex-col gap-10 md:gap-14">
          {/* Banner masthead — the provided brand banner */}
          <motion.div
            initial={BANNER_HIDDEN}
            animate={BANNER_SHOWN}
            transition={BANNER_TRANSITION}
            className="relative w-full"
          >
            <div className="relative overflow-hidden border border-gold/30">
              <motion.img
                src={BANNER_URL}
                alt="The Visa Atelier — Crafting Your Global Journey"
                style={{ y, scale }}
                className="w-full h-auto block"
                data-testid="hero-banner-image"
              />
              {/* Subtle gold hairline frame */}
              <div className="pointer-events-none absolute inset-0 border border-gold/10" />
            </div>
            {/* Corner marks */}
            <div className="pointer-events-none absolute -top-2 -left-2 w-4 h-4 border-t border-l border-gold" />
            <div className="pointer-events-none absolute -top-2 -right-2 w-4 h-4 border-t border-r border-gold" />
            <div className="pointer-events-none absolute -bottom-2 -left-2 w-4 h-4 border-b border-l border-gold" />
            <div className="pointer-events-none absolute -bottom-2 -right-2 w-4 h-4 border-b border-r border-gold" />
          </motion.div>

          {/* Tagline row (wordmark hidden — brand name already appears in banner) */}
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7"></div>

            <div className="col-span-12 md:col-span-5">
              <MaskedLine delay={0.85}>
                <p className="text-ivory/70 text-base md:text-lg leading-relaxed max-w-md">
                  Professional guidance for UK visas, OCI applications, and
                  Worldwide visa services.
                </p>
              </MaskedLine>
            </div>
          </div>
        </motion.div>

        {/* Bottom action row */}
        <motion.div
          initial={CTA_HIDDEN}
          animate={CTA_SHOWN}
          transition={CTA_TRANSITION}
          className="absolute bottom-8 md:bottom-10 left-6 md:left-10 right-6 md:right-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-4"
        >
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="#book"
              data-testid="hero-cta-enquire"
              className="inline-flex items-center gap-4 px-10 py-5 bg-gold text-navy font-mono-ui font-bold text-base md:text-lg tracking-[0.22em] uppercase hover:bg-ivory transition-colors"
            >
              Begin an Application
              <span aria-hidden>→</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
